import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { validateEntityArray, logger } from '@repo/shared-utils';
import api from '../../services/api';

const systemHealthExceptionQueueItemSchema = z.object({
  id: z.string(),
  partition: z.literal('system_health'),
  title: z.string(),
  detail: z.string(),
  severity: z.enum(['Critical', 'High', 'Medium']),
  route: z.string(),
  resolvableViaGovernance: z.boolean(),
  findingType: z.string(),
  fingerprint: z.string(),
  entityId: z.string(),
  entityType: z.string(),
});

export type SystemHealthExceptionQueueItem = z.infer<typeof systemHealthExceptionQueueItemSchema>;

/** Active integrity findings for the exception queue (GET /system/integrity-exceptions). */
export const useSystemHealthIntegrityExceptions = (
  filters?: { operatingOrganizationId?: string },
  options?: { enabled?: boolean },
) => {
  return useQuery({
    enabled: options?.enabled !== false,
    queryKey: ['systemHealth', 'integrityExceptions', filters ?? {}],
    queryFn: async () => {
      const data = await api.systemHealth.integrityExceptions.list(filters);
      const validation = validateEntityArray(systemHealthExceptionQueueItemSchema, data);
      if (!validation.valid) {
        logger.warn('System health integrity exceptions response did not match schema', {
          errors: validation.errors,
        });
        return [] as SystemHealthExceptionQueueItem[];
      }
      return validation.data as SystemHealthExceptionQueueItem[];
    },
    staleTime: 60 * 1000,
  });
};

export const useAcknowledgeIntegrityBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      findingType: string;
      resolutionReason: string;
      batchId?: string;
      impactSummary?: string;
      operatingOrganizationId?: string;
      impactedRows: { entityId: string; fingerprint: string }[];
    }) => api.systemHealth.integrityAcknowledgements.batch(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemHealth', 'integrityExceptions'] });
      queryClient.invalidateQueries({ queryKey: ['governance', 'logs'] });
    },
  });
};
